/** @jsx jsx */

import { Colors, Icon, Tag, Text } from '@zapier/design-system';
import { css, jsx } from '@emotion/core';

import ActionsDropdownMenu from 'app/developer-v3/components/ActionsDropdownMenu';
import { ZAPIER_ACCOUNT_ID, ZAPIER_APP_BASE_URL } from 'app/common/constants';

import {
  ActionStatus,
  CustomAction as CustomActionType,
} from 'app/developer-v3/types/customAction';

const Styles = {
  action: css`
    align-items: center;
    background: ${Colors.PrimeWhite};
    border: 1px solid ${Colors.GrayWarm4};
    border-radius: 3px;
    display: flex;
    padding: 20px;
    gap: 15px;
    margin-top: 15px;
  `,
  actionText: css`
    flex: 1;
  `,
};

type Props = {
  description: string;
  id: number;
  isMakingPublic: boolean;
  isPublic: boolean;
  name: string;
  accountId: number;
  status: ActionStatus;
  makePublic: ({ customActionId: number }) => Promise<CustomActionType>;
};

function CustomAction({
  description,
  id,
  isMakingPublic,
  isPublic,
  makePublic,
  name,
  accountId,
  status,
}: Props) {
  const createdByZapier = accountId === ZAPIER_ACCOUNT_ID;
  const reviewStatus =
    status === ActionStatus.PENDING_REVIEW ? (
      <Tag variant="BrandPeach">Pending Review</Tag>
    ) : null;

  return (
    <li css={Styles.action} role="presentation">
      <Icon color={createdByZapier ? 'UiBrand' : null} name="navCustomAction" />
      <div css={Styles.actionText}>
        <div>
          Custom Action {createdByZapier ? ' - Created by Zapier' : null}{' '}
          {reviewStatus}
        </div>
        <div>
          <Text type="Body4">
            {name} {isPublic ? '(Public)' : null}
          </Text>
          <Text margin="0px 0px 0px 10px">{description}</Text>
        </div>
      </div>
      <ActionsDropdownMenu
        items={[
          {
            label: 'View in Custom Action library',
            onChoose: () => {
              window.open(
                `${ZAPIER_APP_BASE_URL}/app/extensions/actions/${id}`,
                '_blank'
              );
            },
            value: 'app-extension-link',
          },
          {
            isConfirmationRequired: true,
            isDisabled:
              isPublic ||
              isMakingPublic ||
              status === ActionStatus.PENDING_REVIEW,
            label: 'Make public',
            modalTextContent:
              'Are you sure you want make it public? This cannot be undone.',
            modalTitle: `Make Custom Action ${name} public`,
            onChoose: async () => makePublic({ customActionId: id }),
            value: 'app-extension-make-public',
          },
        ]}
      />
    </li>
  );
}

export default CustomAction;
